import React, { useCallback, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import HistoryRoundedIcon from '@material-ui/icons/HistoryRounded';
import { Conversation } from 'features/ai-assistant/components';
import { AIProspectingResultsTable } from 'features/ai-assistant/components/AIProspectingResultsTable';
import { MessageMetaData } from 'features/ai-assistant/components/MessageMetaData';
import { useSourcingResults } from 'features/ai-assistant/hooks';
import usePageTitle from 'hooks/usePageTitle';
import { SECONDARY_API_CLIENT } from 'services/API';
import { FPIconButton, When } from 'UI/components/atoms';
import DualPanelLayout from 'UI/components/templates/DualPanelLayout/DualPanelLayout';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { PageTitles } from 'UI/constants/defaults';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { SvgNoActivities } from 'UI/res';
import { flexAlignCenterJustifyCenter } from 'UI/utils/styles';

import { ConversationsModal } from './components';
import { AI_PROSPECTING_ASSISTANT_BASE_PATH, COPIES } from './Prospecting.constants';
import { useStyles } from './Prospecting.styled';

export const Prospecting = () => {
  usePageTitle({ title: PageTitles.Sourcing });
  const classes = useStyles();
  const [isConversationsModalOpen, setIsConversationsModalOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationKey, setConversationKey] = useState(0);

  const {
    activeResponseId,
    entityType,
    handleMessageReplied: handleSourcingMessageReplied,
    handleMessagesLoaded,
    handleMetaDataClick,
    handleSessionSelected,
    reset: resetActiveResponse
  } = useSourcingResults();

  const metaDataComponent = useCallback(
    ({ data }) => (
      <MessageMetaData
        entityType={entityType}
        message={data}
        onClick={handleMetaDataClick}
        isActive={data?.id === activeResponseId}
      />
    ),
    [activeResponseId, entityType, handleMetaDataClick]
  );

  const handleMessageReplied = useCallback(
    responseData => {
      handleSourcingMessageReplied(responseData);
      setSessionId(prevSessionId => prevSessionId || responseData?.sessionId || null);
    },
    [handleSourcingMessageReplied]
  );

  const handleNewChat = useCallback(() => {
    setSessionId(null);
    resetActiveResponse();
    setConversationKey(prev => prev + 1);
  }, [resetActiveResponse]);

  const handleSelectConversationFromHistory = useCallback(
    ({ sessionId: sessionID, type }) => {
      setSessionId(sessionID);
      resetActiveResponse();
      handleSessionSelected({ type });
      setIsConversationsModalOpen(false);
    },
    [resetActiveResponse, handleSessionSelected]
  );

  const handleDeleteConversation = useCallback(
    async chatId => {
      if (sessionId === chatId) {
        setSessionId(null);
        resetActiveResponse();
      }
    },
    [sessionId, resetActiveResponse]
  );

  const handleOpenConversationsModal = () => setIsConversationsModalOpen(true);
  const handleCloseConversationsModal = () => setIsConversationsModalOpen(false);

  return (
    <DualPanelLayout
      storageKey={PERSISTENCE_KEYS.LAYOUT_WIDTHS.SOURCING}
      leftPanel={{
        header: {
          Header: (
            <div className={classes.leftHeader}>
              <Typography variant="h6">{COPIES.title}</Typography>

              <div style={{ ...flexAlignCenterJustifyCenter }}>
                <FPIconButton
                  size={24}
                  onClick={handleNewChat}
                  color="primary"
                  tooltipProps={{
                    title: COPIES.newChat
                  }}
                >
                  <AddIcon />
                </FPIconButton>
                <FPIconButton
                  size={24}
                  onClick={handleOpenConversationsModal}
                  tooltipProps={{
                    title: COPIES.history
                  }}
                >
                  <HistoryRoundedIcon />
                </FPIconButton>
              </div>
            </div>
          )
        },
        Content: (
          <div className={classes.aiAssistantContainer}>
            <Conversation
              key={conversationKey}
              apiInstance={SECONDARY_API_CLIENT}
              basePath={AI_PROSPECTING_ASSISTANT_BASE_PATH}
              canSelectAIModels={false}
              composerPlaceholder={COPIES.composerPlaceholder}
              customEmptyState={
                <EmptyPlaceholder
                  customEmptyState={<SvgNoActivities height={200} />}
                  title={COPIES.emptyConversation.title}
                  subtitle={COPIES.emptyConversation.subtitle}
                />
              }
              messageFullWidth
              metaDataComponent={metaDataComponent}
              onMessageReplied={handleMessageReplied}
              onMessagesLoaded={handleMessagesLoaded}
              sessionId={sessionId}
            />

            <When condition={isConversationsModalOpen}>
              <ConversationsModal
                onClose={handleCloseConversationsModal}
                open
                onDelete={handleDeleteConversation}
                onSelect={handleSelectConversationFromHistory}
                sessionID={sessionId}
              />
            </When>
          </div>
        )
      }}
      rightPanel={{
        header: {
          Header: <Typography variant="h6">{COPIES.rightPanelTitle}</Typography>
        },
        Content: (
          <AIProspectingResultsTable
            key={`${entityType}-${activeResponseId}`}
            entityType={entityType}
            responseId={activeResponseId}
            shouldScrollOnNavigate
          />
        )
      }}
    />
  );
};
